import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Button,
  Container,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  InputAdornment,
  Chip,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  FormControlLabel,
  Switch,
  Alert,
  Divider,
  Paper
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Webhook as WebhookIcon,
  PlayArrow as TestIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon
} from "@mui/icons-material";
import Header from "../components/Header";
import ObjectCard from "../components/Common/ObjectCard";
import axios from "axios";
import { useApi } from '../hooks/useApi'
import { v4 as uuidv4 } from 'uuid';

// Constantes para eventos de webhook
const WEBHOOK_EVENTS = {
  DOCUMENT_RECEIVED: {
    value: 'DOCUMENT_RECEIVED',
    label: 'Documento Recibido',
    description: 'Se dispara cuando un nuevo documento es recibido en el sistema',
    icon: '📥',
    color: 'info'
  },
  DOCUMENT_PROCESSED_SUCCESSFUL: {
    value: 'DOCUMENT_PROCESSED_SUCCESSFUL',
    label: 'Procesamiento Exitoso',
    description: 'Se dispara cuando un documento se procesa correctamente',
    icon: '✅',
    color: 'success'
  },
  DOCUMENT_PROCESSED_FAILED: {
    value: 'DOCUMENT_PROCESSED_FAILED',
    label: 'Procesamiento Fallido',
    description: 'Se dispara cuando falla el procesamiento de un documento',
    icon: '❌',
    color: 'error'
  }
};

const HTTP_METHODS = ['POST', 'GET', 'PUT', 'PATCH', 'DELETE'];

export default function WebhookManager() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editWebhook, setEditWebhook] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEvent, setFilterEvent] = useState("all");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedWebhookMenu, setSelectedWebhookMenu] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [showTestAlert, setShowTestAlert] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const { callApi, fetching, error } = useApi();


  const WEBHOOK_SUBSCRIPTIONS_QUERY = `
  query ListDocuments {
    listWebhookSubscription(orderBy: "createdAt", orderDirection: "DESC") {
        totalCount
        items {
            id
            title
            url
            event
            method
            headers {
                key
                value
            }
        }
    }
}`

const WEBHOOK_SUBSCRIPTIONS_MUTATION = `
mutation SaveWebhookSubscription($input: WebhookSubscriptionInput!) {
    saveWebhookSubscription(input: $input) {
        id
        title
        description
        url
        event
        enabled
        method
        retryAttempts
        retryDelay
        timeout
        lastTriggered
        successCount
        failureCount
        createdAt
        updatedAt
    }
}
`

  // Valores por defecto para un nuevo webhook
  const getDefaultWebhook = () => ({
    title: "",
    description: "",
    url: "",
    event: "DOCUMENT_RECEIVED",
    enabled: true,
    method: "POST",
    headers: {
      'Content-Type': 'application/json'
    },
    retryAttempts: 3,
    retryDelay: 5000,
    timeout: 30000,
    successCount: 0,
    failureCount: 0
  });

  // Generar UUID
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Obtener icono para el evento
  const getEventIcon = (event) => {
    return WEBHOOK_EVENTS[event]?.icon || '🔗';
  };

  // Obtener color variant para la card según el evento
  const getCardVariant = (webhook) => {
    if (!webhook.enabled) return 'default';
    return WEBHOOK_EVENTS[webhook.event]?.color || 'default';
  };

  // Obtener tags del webhook
  const getWebhookTags = (webhook) => {
    const tags = [];
    
    // Evento - versión corta para ahorrar espacio
    const eventInfo = WEBHOOK_EVENTS[webhook.event];
    if (eventInfo) {
      // Usar versión más corta del label
      const shortLabel = eventInfo.label.split(' ')[0];
      tags.push(shortLabel);
    }
    
    // Método HTTP
    tags.push(webhook.method || 'POST');
    
    // Estado de salud - versión simplificada
    if (webhook.successCount > 0 || webhook.failureCount > 0) {
      const successRate = (webhook.successCount / (webhook.successCount + webhook.failureCount) * 100).toFixed(0);
      if (successRate >= 95) {
        tags.push('OK');
      } else if (successRate >= 80) {
        tags.push('!');
      } else {
        tags.push('ERR');
      }
    }
    
    return tags;
  };

  // Formatear descripción del webhook
  const getWebhookDescription = (webhook) => {
    if (webhook.description) {
      return webhook.description;
    }
    // Acortar la URL para que se ajuste mejor
    const url = webhook.url || '';
    const maxLength = 80;
    if (url.length > maxLength) {
      return `${url.substring(0, maxLength)}...`;
    }
    return url;
  };

  // Traer datos del backend
  const fetchWebhooks = async () => {
    setLoading(true);
    try {
      const data = await callApi(WEBHOOK_SUBSCRIPTIONS_QUERY);
      setWebhooks(
        data.listWebhookSubscription.items.map(item => ({
          ...item,
          enabled: item.enabled === true || item.enabled === "true"
        }))
      );
    } catch (err) {
      console.error("Error fetching webhooks:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWebhooks();
  }, []);

  // Filtrar webhooks basado en búsqueda y filtros
  const filteredWebhooks = webhooks.filter(webhook => {
    const matchSearch = 
      webhook.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      webhook.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      webhook.url?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEvent = filterEvent === "all" || webhook.event === filterEvent;
    return matchSearch && matchEvent;
  });

  // Handler para abrir el diálogo de edición
  const handleOpenEdit = (webhookId) => {
    const webhookToEdit = webhooks.find(w => w.id === webhookId);
    if (webhookToEdit) {
      setEditWebhook({
        ...webhookToEdit,
        headers: webhookToEdit.headers || { 'Content-Type': 'application/json' }
      });
      setOpenDialog(true);
    }
  };

  // Handler para crear nuevo webhook
  const handleCreateNew = () => {
    setEditWebhook(getDefaultWebhook());
    setOpenDialog(true);
  };

  // Handler para editar desde el menú
  const handleEdit = (webhook) => {
    if (webhook && webhook.id) {
      handleOpenEdit(webhook.id);
    }
    handleCloseMenu();
  };

  // Handler para duplicar webhook
  const handleDuplicate = (webhook) => {
    if (webhook) {
      const duplicated = {
        ...webhook,
        id: undefined,
        title: `${webhook.title} (copia)`,
        description: webhook.description ? `${webhook.description} - Copia` : '',
        successCount: 0,
        failureCount: 0,
        lastTriggered: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setEditWebhook(duplicated);
      setOpenDialog(true);
    }
    handleCloseMenu();
  };

  // Handler para eliminar webhook
  const handleDelete = async (id) => {
    if (window.confirm("¿Está seguro de que desea eliminar este webhook?")) {
      try {
        await axios.delete(`http://localhost:4000/webhooks/${id}`);
        fetchWebhooks();
      } catch (err) {
        console.error("Error deleting webhook:", err);
      }
    }
    handleCloseMenu();
  };


  // Handler para toggle de activación
  const handleToggleActive = async (webhookId, newState) => {
    const webhook = webhooks.find(w => w.id === webhookId);
    if (webhook) {
      try {
        const updatedWebhook = { 
          ...webhook, 
          enabled: newState.toString(),
          updatedAt: new Date().toISOString()
        };
        await callApi(WEBHOOK_SUBSCRIPTIONS_MUTATION, { input: updatedWebhook });
        fetchWebhooks();
      } catch (err) {
        console.error("Error toggling webhook:", err);
      }
    }
  };

  // Handler para probar webhook
  const handleTestWebhook = async (webhook) => {
    setShowTestAlert(true);
    setTestResult({ loading: true, webhook: webhook.title });
    
    try {
      // Simular prueba de webhook
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setTestResult({
        success: true,
        message: `Webhook "${webhook.title}" probado exitosamente`,
        webhook: webhook.title
      });
      
      // Actualizar contadores
      const updatedWebhook = {
        ...webhook,
        successCount: (webhook.successCount || 0) + 1,
        lastTriggered: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      await axios.put(`http://localhost:4000/webhooks/${webhook.id}`, updatedWebhook);
      fetchWebhooks();
    } catch (err) {
      setTestResult({
        success: false,
        message: `Error al probar webhook "${webhook.title}"`,
        webhook: webhook.title
      });
    }
    
    setTimeout(() => setShowTestAlert(false), 5000);
    handleCloseMenu();
  };

  // Handler para copiar URL
  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
    handleCloseMenu();
  };

  

  // Handler para guardar cambios
  const handleSave = async () => {
    try {
      const now = new Date().toISOString();
      
      // Preparar headers como objeto
      let headers = {};
      if (typeof editWebhook.headers === 'string') {
        try {
          headers = JSON.parse(editWebhook.headers);
        } catch {
          headers = { 'Content-Type': 'application/json' };
        }
      } else {
        headers = editWebhook.headers || { 'Content-Type': 'application/json' };
      }
      
      const webhookData = {
        ...editWebhook,
        headers,
        retryAttempts: parseInt(editWebhook.retryAttempts) || 3,
        retryDelay: parseInt(editWebhook.retryDelay) || 5000,
        timeout: parseInt(editWebhook.timeout) || 30000,
        enabled: editWebhook.enabled.toString() 
      };

      if (editWebhook.id) {
        // Actualizar webhook existente
        const updatedWebhook = {
          ...webhookData,
          updatedAt: new Date().toISOString()
        };
        await callApi(WEBHOOK_SUBSCRIPTIONS_MUTATION, { input: updatedWebhook });
      } else {
        // Crear nuevo webhook
        const newWebhook = {
          ...webhookData,
          id: generateUUID(),
          successCount: 0,
          failureCount: 0,
          lastTriggered: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          enabled: 'true'
        };
        await callApi(WEBHOOK_SUBSCRIPTIONS_MUTATION, { input: newWebhook });
      }
      
      setOpenDialog(false);
      setEditWebhook(null);
      fetchWebhooks();
    } catch (err) {
      console.error("Error saving webhook:", err);
    }
  };

  // Handler para cerrar diálogo
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditWebhook(null);
  };

  // Menu handlers
  const handleOpenMenu = (event, webhook) => {
    setAnchorEl(event.currentTarget);
    setSelectedWebhookMenu(webhook);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedWebhookMenu(null);
  };

  return (
    <div>
      <Header />

      <Container 
        maxWidth="lg" 
        sx={{ 
          mt: 4, 
          mb: 4,
          px: { xs: 2, sm: 3 }
        }}
      >
        {/* Test Alert */}
        {showTestAlert && testResult && (
          <Alert 
            severity={testResult.loading ? "info" : testResult.success ? "success" : "error"}
            icon={testResult.loading ? <ScheduleIcon /> : testResult.success ? <CheckIcon /> : <ErrorIcon />}
            sx={{ mb: 2 }}
          >
            {testResult.loading ? `Probando webhook "${testResult.webhook}"...` : testResult.message}
          </Alert>
        )}

        {/* Copied URL Alert */}
        {copiedUrl && (
          <Alert severity="success" icon={<CopyIcon />} sx={{ mb: 2 }}>
            URL copiada al portapapeles
          </Alert>
        )}

        {/* Header Section */}
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems={{ xs: 'stretch', sm: 'center' }} 
          mb={3}
          spacing={2}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              <WebhookIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
              Gestión de Webhooks
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configura y administra los webhooks para recibir notificaciones de eventos
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              alignSelf: { xs: 'stretch', sm: 'auto' }
            }}
          >
            Nuevo Webhook
          </Button>
        </Stack>

        {/* Filters and Search Bar */}
        <Stack 
          direction="column" 
          spacing={2} 
          mb={3}
        >
          <TextField
            placeholder="Buscar webhooks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
            sx={{ maxWidth: { sm: 500 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 1 
          }}>
            <Chip
              label="Todos"
              onClick={() => setFilterEvent("all")}
              color={filterEvent === "all" ? "primary" : "default"}
              variant={filterEvent === "all" ? "filled" : "outlined"}
              size="small"
            />
            {Object.values(WEBHOOK_EVENTS).map(event => (
              <Chip
                key={event.value}
                label={`${event.icon} ${event.label.split(' ')[0]}`}
                onClick={() => setFilterEvent(event.value)}
                color={filterEvent === event.value ? event.color : "default"}
                variant={filterEvent === event.value ? "filled" : "outlined"}
                size="small"
              />
            ))}
          </Box>
        </Stack>

        {/* Stats */}
        <Stack 
          direction="row" 
          spacing={1} 
          mb={3}
          flexWrap="wrap"
          useFlexGap
        >
          <Chip
            label={`${filteredWebhooks.length} webhooks`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`${filteredWebhooks.filter(w => w.enabled).length} activos`}
            size="small"
            color="success"
            variant="outlined"
          />
          <Chip
            label={`${filteredWebhooks.filter(w => w.lastTriggered).length} utilizados`}
            size="small"
            color="info"
            variant="outlined"
          />
        </Stack>

        {/* Cards Grid - MÁXIMO 2 COLUMNAS */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',           // 1 columna en móvil
            sm: 'repeat(2, 1fr)' // 2 columnas en tablet y desktop
          },
          gap: 2,
          width: '100%'
        }}>
          {loading ? (
            [...Array(4)].map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                height={180}
                sx={{ borderRadius: 3 }}
              />
            ))
          ) : filteredWebhooks.length > 0 ? (
            filteredWebhooks.map((webhook) => (
              <Box key={webhook.id} position="relative">
                <ObjectCard
                  id={webhook.id}
                  icon={getEventIcon(webhook.event)}
                  title={webhook.title || "Sin título"}
                  description={getWebhookDescription(webhook)}
                  lastUpdated={webhook.lastTriggered || webhook.updatedAt}
                  isEnabled={webhook.enabled !== false}
                  tags={getWebhookTags(webhook)}
                  onClick={handleOpenEdit}
                  onToggle={handleToggleActive}
                  variant={getCardVariant(webhook)}
                />

                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': { boxShadow: 2 }
                  }}
                  onClick={(e) => handleOpenMenu(e, webhook)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            ))
          ) : (
            <Box sx={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              py: 8
            }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No se encontraron webhooks
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm ? "Intenta con otros términos de búsqueda" : "Crea tu primer webhook"}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
        >
          <MenuItem onClick={() => handleEdit(selectedWebhookMenu)}>
            Editar
          </MenuItem>
          <MenuItem onClick={() => handleTestWebhook(selectedWebhookMenu)}>
            <TestIcon sx={{ mr: 1, fontSize: 20 }} />
            Probar Webhook
          </MenuItem>
          <MenuItem onClick={() => handleCopyUrl(selectedWebhookMenu?.url)}>
            <CopyIcon sx={{ mr: 1, fontSize: 20 }} />
            Copiar URL
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => handleDuplicate(selectedWebhookMenu)}>
            Duplicar
          </MenuItem>
          <MenuItem
            onClick={() => handleDelete(selectedWebhookMenu?.id)}
            sx={{ color: 'error.main' }}
          >
            Eliminar
          </MenuItem>
        </Menu>

        {/* Edit/Create Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {editWebhook?.id ? `Editar: ${editWebhook.title}` : "Nuevo Webhook"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} mt={2}>
              {/* Información básica */}
              <TextField
                label="Título"
                value={editWebhook?.title || ""}
                onChange={(e) => setEditWebhook({ ...editWebhook, title: e.target.value })}
                fullWidth
                required
                helperText="Nombre descriptivo para identificar el webhook"
              />

              <TextField
                label="Descripción"
                value={editWebhook?.description || ""}
                onChange={(e) => setEditWebhook({ ...editWebhook, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
                inputProps={{ maxLength: 160 }}
                helperText={`${editWebhook?.description?.length || 0}/160 caracteres`}
              />

              {/* Configuración del endpoint */}
              <TextField
                label="URL del Webhook"
                value={editWebhook?.url || ""}
                onChange={(e) => setEditWebhook({ ...editWebhook, url: e.target.value })}
                fullWidth
                required
                placeholder="https://ejemplo.com/webhook"
                helperText="URL donde se enviarán las notificaciones"
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Evento"
                  value={editWebhook?.event || "DOCUMENT_RECEIVED"}
                  onChange={(e) => setEditWebhook({ ...editWebhook, event: e.target.value })}
                  fullWidth
                  select
                  required
                >
                  {Object.values(WEBHOOK_EVENTS).map(event => (
                    <MenuItem key={event.value} value={event.value}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{event.icon}</span>
                        <Box>
                          <Typography variant="body2">{event.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {event.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Método HTTP"
                  value={editWebhook?.method || "POST"}
                  onChange={(e) => setEditWebhook({ ...editWebhook, method: e.target.value })}
                  fullWidth
                  select
                >
                  {HTTP_METHODS.map(method => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              {/* Configuración de reintentos */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Configuración de Reintentos
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={2}>
                  <TextField
                    label="Intentos"
                    type="number"
                    value={editWebhook?.retryAttempts || 3}
                    onChange={(e) => setEditWebhook({ ...editWebhook, retryAttempts: parseInt(e.target.value) })}
                    fullWidth
                    InputProps={{
                      inputProps: { min: 0, max: 10 }
                    }}
                    helperText="Número de reintentos en caso de fallo"
                  />
                  <TextField
                    label="Delay (ms)"
                    type="number"
                    value={editWebhook?.retryDelay || 5000}
                    onChange={(e) => setEditWebhook({ ...editWebhook, retryDelay: parseInt(e.target.value) })}
                    fullWidth
                    InputProps={{
                      inputProps: { min: 1000, max: 60000, step: 1000 }
                    }}
                    helperText="Tiempo entre reintentos"
                  />
                  <TextField
                    label="Timeout (ms)"
                    type="number"
                    value={editWebhook?.timeout || 30000}
                    onChange={(e) => setEditWebhook({ ...editWebhook, timeout: parseInt(e.target.value) })}
                    fullWidth
                    InputProps={{
                      inputProps: { min: 5000, max: 120000, step: 5000 }
                    }}
                    helperText="Tiempo máximo de espera"
                  />
                </Stack>
              </Paper>

              {/* Headers personalizados */}
              <TextField
                label="Headers (JSON)"
                value={typeof editWebhook?.headers === 'object' ? 
                  JSON.stringify(editWebhook.headers, null, 2) : 
                  editWebhook?.headers || '{\n  "Content-Type": "application/json"\n}'}
                onChange={(e) => setEditWebhook({ ...editWebhook, headers: e.target.value })}
                fullWidth
                multiline
                rows={4}
                placeholder='{"Content-Type": "application/json"}'
                helperText="Headers HTTP personalizados en formato JSON"
              />

              {/* Estado activo/inactivo */}
              <FormControlLabel
                control={
                  <Switch
                    checked={editWebhook?.enabled !== false}
                    onChange={(e) => setEditWebhook({ ...editWebhook, enabled: e.target.checked })}
                  />
                }
                label="Webhook activo"
              />

              {/* Estadísticas (solo en modo edición) */}
              {editWebhook?.id && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Estadísticas
                  </Typography>
                  <Stack direction="row" spacing={3} mt={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Éxitos
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {editWebhook.successCount || 0}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Fallos
                      </Typography>
                      <Typography variant="h6" color="error.main">
                        {editWebhook.failureCount || 0}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Última ejecución
                      </Typography>
                      <Typography variant="body2">
                        {editWebhook.lastTriggered ? 
                          new Date(editWebhook.lastTriggered).toLocaleString('es-ES') : 
                          'Nunca'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDialog}>
              Cancelar
            </Button>
            {editWebhook?.id && (
              <Button
                variant="outlined"
                onClick={() => handleTestWebhook(editWebhook)}
                startIcon={<TestIcon />}
              >
                Probar
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!editWebhook?.title || !editWebhook?.url}
            >
              {editWebhook?.id ? "Guardar Cambios" : "Crear Webhook"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </div>
  );
}